import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, Chip } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import type { BookingResponse, BookingStatusValue } from '@lov/shared';
import { getISTToday, formatISTLocale } from '@lov/shared';
import { useCustomerCheckIn } from '../hooks/useCustomerCheckIn';

interface BookingCardProps {
  booking: BookingResponse;
  onCancel?: (id: string) => void;
  showVenueLink?: boolean;
}

const STATUS_STYLES: Record<
  BookingStatusValue,
  { bg: string; text: string; borderColor: string; label: string }
> = {
  PENDING: {
    bg: 'rgba(251, 146, 60, 0.08)', // Orange theme
    text: '#FB923C',
    borderColor: 'rgba(251, 146, 60, 0.2)',
    label: 'Pending',
  },
  CONFIRMED: {
    bg: 'rgba(16, 185, 129, 0.08)', // Green theme
    text: '#10B981',
    borderColor: 'rgba(16, 185, 129, 0.2)',
    label: 'Confirmed',
  },
  CANCELLED: {
    bg: 'rgba(239, 68, 68, 0.08)', // Red
    text: '#EF4444',
    borderColor: 'rgba(239, 68, 68, 0.2)',
    label: 'Cancelled',
  },
  COMPLETED: {
    bg: 'rgba(168, 85, 247, 0.08)', // Purple theme
    text: '#A855F7',
    borderColor: 'rgba(168, 85, 247, 0.2)',
    label: 'Completed',
  },
  NO_SHOW: {
    bg: 'rgba(107, 114, 128, 0.08)', // Gray
    text: '#6B7280',
    borderColor: 'rgba(107, 114, 128, 0.2)',
    label: 'No Show',
  },
  CHECKED_IN: {
    bg: 'rgba(59, 130, 246, 0.08)', // Blue
    text: '#3B82F6',
    borderColor: 'rgba(59, 130, 246, 0.2)',
    label: 'Checked In',
  },
};

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasFailedPayment',
})<{ hasFailedPayment?: boolean }>(({ theme, hasFailedPayment }) => ({
  backgroundColor: hasFailedPayment
    ? alpha(theme.palette.error.main, 0.02)
    : theme.palette.background.paper,
  borderRadius: 16,
  overflow: 'hidden',
  border: hasFailedPayment
    ? `2px solid ${alpha(theme.palette.error.main, 0.3)}`
    : `1px solid ${theme.palette.surface.glassBorder}`,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  display: 'flex',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    borderColor: hasFailedPayment
      ? alpha(theme.palette.error.main, 0.5)
      : alpha(theme.palette.venue.gaming, 0.15),
  },
}));

const ImageSection = styled(Box)({
  position: 'relative',
  width: 128,
  flexShrink: 0,
  backgroundColor: '#F5F5F5',
  overflow: 'hidden',
  cursor: 'pointer',

  '@media (min-width: 768px)': {
    width: 160,
  },
});

const CoverImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  minHeight: 120,
});

const ImagePlaceholder = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  minHeight: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.disabled,
}));

const ContentSection = styled(Box)({
  flex: 1,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 8,
});

const VenueName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'clickable',
})<{ clickable?: boolean }>(({ theme, clickable }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  cursor: clickable ? 'pointer' : 'default',
  transition: 'color 0.2s ease',

  ...(clickable && {
    '&:hover': {
      color: theme.palette.venue.gaming,
    },
  }),
}));

const CityName = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) =>
    prop !== 'statusColor' && prop !== 'statusBg' && prop !== 'borderColor',
})<{ statusColor: string; statusBg: string; borderColor: string }>(
  ({ statusColor, statusBg, borderColor }) => ({
    height: 24,
    fontSize: '0.6875rem',
    fontWeight: 600,
    fontFamily: '"IBM Plex Mono", monospace',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: statusBg,
    color: statusColor,
    border: `1px solid ${borderColor}`,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  })
);

const DateTimeRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  marginTop: 12,
});

const DateTimeItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontFamily: '"IBM Plex Sans", sans-serif',

  '& svg': {
    fontSize: 16,
  },
}));

const InventoryItems = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 8,
});

const InventoryChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.6875rem',
  backgroundColor: alpha(theme.palette.text.primary, 0.06),
  color: theme.palette.text.secondary,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const GuestCount = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: 8,
}));

const Footer = styled(Box)({
  marginTop: 'auto',
  paddingTop: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Price = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: theme.palette.venue.gaming,
}));

const Actions = styled(Box)({
  display: 'flex',
  gap: 8,
});

const ActionButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'disabled',
})<{ variant?: 'cancel' | 'view' | 'checkin' | 'payment' | 'invoice'; disabled?: boolean }>(
  ({ theme, variant, disabled }) => ({
    padding: '6px 12px',
    fontSize: '0.875rem',
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontWeight: 500,
    borderRadius: 8,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: disabled ? 0.5 : 1,

    ...(variant === 'cancel' && {
      color: theme.palette.error.main,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: !disabled ? alpha(theme.palette.error.main, 0.08) : 'transparent',
        color: !disabled ? theme.palette.error.dark : theme.palette.error.main,
      },
    }),

    ...(variant === 'view' && {
      color: theme.palette.venue.gaming,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: !disabled ? alpha(theme.palette.venue.gaming, 0.08) : 'transparent',
        color: theme.palette.venue.gaming,
      },
    }),

    ...(variant === 'checkin' && {
      color: '#fff',
      backgroundColor: theme.palette.success.main,
      '&:hover': {
        backgroundColor: !disabled ? theme.palette.success.dark : theme.palette.success.main,
      },
    }),

    ...(variant === 'payment' && {
      color: '#fff',
      backgroundColor: theme.palette.warning.main,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: !disabled ? theme.palette.warning.dark : theme.palette.warning.main,
      },
    }),

    ...(variant === 'invoice' && {
      color: theme.palette.info.main,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: !disabled ? alpha(theme.palette.info.main, 0.08) : 'transparent',
        color: theme.palette.info.dark,
      },
    }),
  })
);

export function BookingCard({ booking, onCancel, showVenueLink = true }: BookingCardProps) {
  const navigate = useNavigate();
  const checkInMutation = useCustomerCheckIn();

  const formatCurrency = (amount: number, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return formatISTLocale(new Date(dateString), {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const statusStyle = STATUS_STYLES[booking.status];
  const canCancel = booking.status === 'PENDING';

  // Check if booking is in the past
  const isPast = new Date(booking.bookingDate) < new Date(new Date().toDateString());

  // Check if booking is TODAY and CONFIRMED (can check in)
  // Use IST date — toISOString() returns UTC which is yesterday's date between midnight and 5:30am IST
  const today = getISTToday();
  const isToday = booking.bookingDate === today;
  const canCheckIn = booking.status === 'CONFIRMED' && isToday;

  // Check if booking has failed payment or needs payment
  // A booking needs payment retry if it's PENDING and was created more than 5 minutes ago
  // (indicating payment was likely attempted but failed or abandoned)
  const hasFailedPayment = booking.status === 'PENDING' && !isPast;
  const createdDate = new Date(booking.createdAt);
  const now = new Date();
  const minutesSinceCreation = (now.getTime() - createdDate.getTime()) / (1000 * 60);
  const needsPaymentRetry = hasFailedPayment && minutesSinceCreation > 5;

  // Use fallback image if coverImage is missing (matching Mobile behavior)
  const venueimageSrc =
    booking.venue.coverImage || `https://picsum.photos/seed/${booking.venue.id}/400/200`;
  const [venueImageError, setVenueImageError] = useState(false);

  const handleCheckIn = () => {
    checkInMutation.mutate({ bookingId: booking.id });
  };

  const handleContinuePayment = () => {
    // Navigate to payment method selection with bookingId and amount
    navigate(`/payments/select?bookingId=${booking.id}&amount=${booking.totalAmount}`);
  };

  const handleViewInvoice = () => {
    navigate(`/bookings/${booking.id}`);
  };

  // Show View Invoice for completed bookings
  const isCompleted = booking.status === 'COMPLETED';

  return (
    <CardContainer data-testid="booking-card" hasFailedPayment={needsPaymentRetry}>
      {/* Venue Image */}
      <ImageSection onClick={() => showVenueLink && navigate(`/venues/${booking.venue.slug}`)}>
        {!venueImageError ? (
          <CoverImage
            src={venueimageSrc}
            alt={booking.venue.name}
            loading="lazy"
            onError={() => setVenueImageError(true)}
          />
        ) : (
          <ImagePlaceholder>
            <ImageOutlinedIcon sx={{ fontSize: 40 }} />
          </ImagePlaceholder>
        )}
      </ImageSection>

      {/* Booking Details */}
      <ContentSection>
        <Header>
          <Box>
            <VenueName
              clickable={showVenueLink}
              onClick={() => showVenueLink && navigate(`/venues/${booking.venue.slug}`)}
            >
              {booking.venue.name}
            </VenueName>
            <CityName>{booking.venue.city?.name}</CityName>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
            {/* Status Badge */}
            <StatusChip
              label={statusStyle.label}
              size="small"
              statusColor={statusStyle.text}
              statusBg={statusStyle.bg}
              borderColor={statusStyle.borderColor}
            />
            {/* Payment Pending Badge */}
            {needsPaymentRetry && (
              <StatusChip
                label="Payment Pending"
                size="small"
                statusColor="#FB923C"
                statusBg="rgba(251, 146, 60, 0.12)"
                borderColor="rgba(251, 146, 60, 0.3)"
              />
            )}
          </Box>
        </Header>

        {/* Date & Time */}
        <DateTimeRow>
          <DateTimeItem>
            <CalendarTodayIcon />
            {formatDate(booking.bookingDate)}
          </DateTimeItem>
          <DateTimeItem>
            <AccessTimeIcon />
            {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
          </DateTimeItem>
        </DateTimeRow>

        {/* Inventory Items */}
        {booking.inventoryItems && booking.inventoryItems.length > 0 && (
          <InventoryItems>
            {booking.inventoryItems
              .filter((item) => item.inventoryItem) // Filter out items with missing inventoryItem
              .map((item) => (
                <InventoryChip
                  key={item.id}
                  label={`${item.inventoryItem.name} x ${item.quantity}`}
                  size="small"
                />
              ))}
          </InventoryItems>
        )}

        {/* Guest Count */}
        {booking.guestCount && (
          <GuestCount>
            {booking.guestCount} guest{booking.guestCount > 1 ? 's' : ''}
          </GuestCount>
        )}

        {/* Price & Actions */}
        <Footer>
          <Price>{formatCurrency(booking.totalAmount, booking.currency)}</Price>

          <Actions>
            {canCheckIn && (
              <ActionButton
                variant="checkin"
                onClick={handleCheckIn}
                disabled={checkInMutation.isPending}
              >
                {checkInMutation.isPending ? 'Checking In...' : 'Check In'}
              </ActionButton>
            )}
            {needsPaymentRetry ? (
              <ActionButton variant="payment" onClick={handleContinuePayment}>
                Continue Payment
              </ActionButton>
            ) : (
              canCancel &&
              !isPast &&
              onCancel && (
                <ActionButton variant="cancel" onClick={() => onCancel(booking.id)}>
                  Cancel
                </ActionButton>
              )
            )}
            {isCompleted && (
              <ActionButton variant="invoice" onClick={handleViewInvoice}>
                View Invoice
              </ActionButton>
            )}
            <ActionButton variant="view" onClick={() => navigate(`/bookings/${booking.id}`)}>
              View Details
            </ActionButton>
          </Actions>
        </Footer>
      </ContentSection>
    </CardContainer>
  );
}
